import { AxiosRequestConfig, CancelToken } from 'axios'
import { Abruftoken } from '@/models/Abruftoken'
import { Befehlsnachricht, BfnStatus } from '@/models/Befehlsnachricht'
import {
  BefehlsnachrichtStandortParts,
  convertStandortToDto,
  createBefehlsnachrichtHash,
  Standort
} from '@repo/ui'
import { AbruftokenRequestDto } from './dtos/abruftoken-request-dto'
import { backendApi } from './clients'
import { BefehlsnachrichtDto } from './dtos/befehlsnachricht-dto'
import { convertBefehlsnachrichtDtoToModel } from './converters/befehlsnachricht-dto-converter'

const buildBfnTokenRequestConfig = (
  abruftoken: Abruftoken,
  cancelToken?: CancelToken
): AxiosRequestConfig => {
  return {
    headers: {
      Abruftoken: abruftoken.abruftoken
    },
    cancelToken
  }
}

export async function getBefehlsnachricht(
  abruftoken: Abruftoken,
  cancelToken?: CancelToken
): Promise<Befehlsnachricht> {
  const url = '/tf/befehlsnachricht'
  const response = await backendApi.get<BefehlsnachrichtDto>(
    url,
    buildBfnTokenRequestConfig(abruftoken, cancelToken)
  )
  return convertBefehlsnachrichtDtoToModel(response.data)
}

export async function postToken(
  zugnummer: number,
  zugriffscode: string
): Promise<Abruftoken> {
  const url = '/tf/abruftoken'
  const dto: AbruftokenRequestDto = { zugnummer, zugriffscode }
  const response = await backendApi.post<Abruftoken>(url, dto)
  return { ...response.data, tokenReceivedDate: new Date() }
}

export async function postStandort(
  abruftoken: Abruftoken,
  standort: Standort,
  bfnId: string
): Promise<void> {
  const url = '/tf/befehlsnachricht/standort'
  const dto = convertStandortToDto(standort)
  const standortHash = await createBefehlsnachrichtHash({
    id: bfnId,
    standort: dto
  } as BefehlsnachrichtStandortParts)
  await backendApi.post<void>(
    url,
    { standort: dto, standortHash: standortHash },
    buildBfnTokenRequestConfig(abruftoken)
  )
}

export async function postStatus(
  abruftoken: Abruftoken,
  status: BfnStatus
): Promise<void> {
  const url = '/tf/befehlsnachricht/status'
  await backendApi.post<void>(
    url,
    { status },
    buildBfnTokenRequestConfig(abruftoken)
  )
}

export async function getBefehlsnachrichtAsPdf(
  abruftoken: Abruftoken,
  manuellWiderrufenAt?: Date
): Promise<{
  blob: Blob
  filename: string
}> {
  const url = manuellWiderrufenAt
    ? `/tf/befehlsnachricht?manuellWiderrufenAt=${manuellWiderrufenAt.toISOString()}`
    : '/tf/befehlsnachricht'
  const requestConfig = buildBfnTokenRequestConfig(abruftoken)

  const response = await backendApi.get(url, {
    ...requestConfig,
    responseType: 'blob',
    headers: {
      ...requestConfig.headers,
      Accept: 'application/pdf'
    }
  })

  const blob = new Blob([response.data], { type: 'application/pdf' })

  let filename = 'Befehlsnachricht.pdf'
  const contentDisposition = response.headers['content-disposition']
  if (contentDisposition) {
    const fileNameMatch = contentDisposition.match(/filename="?([^"]+)"?/)
    if (fileNameMatch && fileNameMatch.length > 1) {
      const [, extractedFilename] = fileNameMatch
      filename = extractedFilename
    }
  }

  return { blob, filename }
}
